import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './HistoryTab.css';
import { ordersAPI, documentsAPI } from '../../services/api';

const HistoryTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [uploading, setUploading] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getUserOrders();
      if (response.success) {
        setOrders(response.data.orders);
      } else {
        setError(response.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'processing': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'failed': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'awaiting_payment': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'refunded': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const handleFileUpload = async (orderId, serviceId, documentType, file) => {
    if (!file) return;

    const uploadKey = `${orderId}-${serviceId}-${documentType}`;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('serviceId', serviceId);
      formData.append('documentType', documentType);
      formData.append('orderId', orderId);

      const response = await documentsAPI.uploadDocument(formData);
      
      if (response.success) {
        setUploadedDocuments(prev => ({
          ...prev,
          [uploadKey]: {
            id: response.data.document.id,
            name: response.data.document.originalName,
            size: response.data.document.fileSize,
            status: response.data.document.status,
            serviceId: serviceId,
            documentType: documentType
          }
        }));
        
        // Refresh orders to get updated document status
        fetchOrders();
      } else {
        setError(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleFileChange = (orderId, serviceId, documentType, event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(orderId, serviceId, documentType, file);
    }
  };

  const handleRemoveDocument = (orderId, serviceId, documentType) => {
    const uploadKey = `${orderId}-${serviceId}-${documentType}`;
    setUploadedDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[uploadKey];
      return newDocs;
    });
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="history-tab">
        <div className="history-header">
          <h2>Order History</h2>
        </div>
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-tab">
        <div className="history-header">
          <h2>Order History</h2>
        </div>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="history-tab">
      <div className="history-header">
        <h2>Order History</h2>
        <p>View and manage your orders</p>
      </div>
      
      {orders.length === 0 ? (
        <div className="no-orders">
          <p>No orders found</p>
          <button onClick={() => navigate('/services')} className="browse-services-btn">
            Browse Services
          </button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order._id} className="order-card">
<div className="order-header">
  <div className="order-info">
    <h3 className="order-number">Order #{order.orderNumber}</h3>

 <span
  className="payment-status"
  onClick={(e) => e.stopPropagation()} 
>
  Payment: {order.paymentStatus.replace(/_/g, ' ')}
</span>

    <p className="order-services">
      {order.items.length} Service{order.items.length > 1 ? 's' : ''}:{' '}
      {order.items.map(item => item?.service?.name).join(', ')}
    </p>

    <p className="order-date">
      Placed on {formatDate(order.createdAt)}
    </p>
  </div>

  <div className="order-right">
    <span
      className="status-badge"
      style={{ backgroundColor: getStatusColor(order.status) }}
    >
      {order.status}
    </span>

  <span
  className="expand-icon"
  onClick={() => toggleOrderExpansion(order._id)}
>
  {expandedOrder === order._id ? '▼' : '▶'}
</span>
  </div>
</div>


              <div className="order-details">
                <div className="order-amount">
                  <span className="amount-label">Total Amount:</span>
                  <span className="amount-value">{formatPrice(order.totalAmount)}</span>
                </div>
              </div>

              {/* Expanded Services and Documents Section */}
              {expandedOrder === order._id && (
                <div className="order-expanded">
                  <div className="services-section">
                    <h4>Services & Required Documents</h4>
                    {order.items.map((item, index) => (
                      <div key={index} className="service-item">
                        <div className="service-header">
                          <h5>{item?.service?.name}</h5>
                          <span className="service-price">₹{item.price} × {item.quantity}</span>
                        </div>
                        <p className="service-description">{item.service.description}</p>
                        
                        {/* Required Documents */}
                        {item.service.requiredDocuments && item.service.requiredDocuments.length > 0 && (
                          <div className="documents-section">
                            <h6>Required Documents:</h6>
                            <div className="documents-grid">
                              {item.service.requiredDocuments.map((doc, docIndex) => {
                                const uploadKey = `${order._id}-${item.service._id}-${doc.type}`;
                                const uploadedDoc = uploadedDocuments[uploadKey];
                                const isUploading = uploading[uploadKey];
                                
                                return (
                                  <div key={docIndex} className="document-card">
                                    <div className="document-header">
                                      <h6>{doc.name}</h6>
                                      <span className={`document-status ${doc.isRequired ? 'required' : 'optional'}`}>
                                        {doc.isRequired ? 'Required' : 'Optional'}
                                      </span>
                                    </div>
                                    
                                    <div className="document-info">
                                      <p><strong>Formats:</strong> {doc.acceptedFormats.join(', ').toUpperCase()}</p>
                                      <p><strong>Max Size:</strong> {doc.maxSize}MB</p>
                                    </div>

                                    {uploadedDoc ? (
                                      <div className="uploaded-document">
                                        <div className="file-info">
                                          <span className="file-name">{uploadedDoc.name}</span>
                                          <span className="file-size">{formatFileSize(uploadedDoc.size)}</span>
                                        </div>
                                        <div className="file-status">
                                          <span className={`status-badge ${uploadedDoc.status}`}>
                                            {uploadedDoc.status}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() => handleRemoveDocument(order._id, item.service._id, doc.type)}
                                          className="remove-btn"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="upload-area">
                                        <input
                                          type="file"
                                          id={`file-${order._id}-${item.service._id}-${doc.type}`}
                                          accept={doc.acceptedFormats.map(format => `.${format}`).join(',')}
                                          onChange={(e) => handleFileChange(order._id, item.service._id, doc.type, e)}
                                          className="file-input"
                                          disabled={isUploading}
                                        />
                                        <label htmlFor={`file-${order._id}-${item.service._id}-${doc.type}`} className="upload-label">
                                          {isUploading ? (
                                            <div className="uploading">
                                              <div className="upload-spinner"></div>
                                              <span>Uploading...</span>
                                            </div>
                                          ) : (
                                            <div className="upload-placeholder">
                                              <span className="upload-icon">📁</span>
                                              <span>Click to upload</span>
                                            </div>
                                          )}
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryTab;
